import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
    FormGroup,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
} from "@mui/material";
import additionalInfo from "../../../Mobx/AdditionalInfo";

const dictionarySections = [
    { value: "images", label: "תמונות" },
    { value: "sentences", label: "משפטים" },
    { value: "definitions", label: "הגדרות" },
    { value: "stories", label: "סיפורים" },
    { value: "trnsAr", label: "תרגום לערבית" },
    { value: "trnsRu", label: "תרגום לרוסית" },
];

const practiceSections = [
    { value: "distractingForTrns", label: "מסיחים עבור תרגום" },
    { value: "distractingForOriginal", label: "מסיחים עבור אוריגינאל" },
    { value: "distractingForDefinitionInEng", label: "מסיחים עבור הגדרות באנגלית" },
    { value: "distractingForDefinitionInHeb", label: "מסיחים עבור הגדרות בעברית" },
    { value: "distractingForWordFamilies", label: "מסיחים עבור משפחות מילים" },
    { value: "sentencesWithMissing", label: "משפטים שחסר בהם מילה באמצע" },
    { value: "sentencesWithLastMissing", label: "משפטים שחסר בהם מילה בסוף" },
    { value: "opposites", label: "הפכים וזוגות" },
    { value: "synonyms", label: "מילים נרדפות" },
];

const SectionSelector = observer(() => {
    const [selectedSections, setSelectedSections] = useState([]);

    useEffect(() => {
        setSelectedSections(additionalInfo.getInitialSectionsToShow());
    }, []);

    const updateSections = (newSections) => {
        setSelectedSections(newSections);
        additionalInfo.setSectionsToShow(newSections);
    };

    const handleChange = (section) => {
        const updated = selectedSections.includes(section)
            ? selectedSections.filter(s => s !== section)
            : [...selectedSections, section];

        updateSections(updated);
    };

    const handleGroupToggle = (group) => {
        const groupValues = group.map(s => s.value);
        const allSelected = groupValues.every(value => selectedSections.includes(value));

        const updated = allSelected
            ? selectedSections.filter(s => !groupValues.includes(s))
            : Array.from(new Set([...selectedSections, ...groupValues]));

        updateSections(updated);
    };

    const isChecked = (value) => selectedSections.includes(value);

    const isGroupChecked = (group) =>
        group.every(({ value }) => selectedSections.includes(value));

    const isGroupIndeterminate = (group) => {
        const groupValues = group.map(s => s.value);
        const selectedCount = groupValues.filter(val => selectedSections.includes(val)).length;
        return selectedCount > 0 && selectedCount < groupValues.length;
    };

    const groupToggleStyle = {
        fontWeight: "bold",
        backgroundColor: "#e0e0e0",
        borderRadius: 1,
        py: 0.5,
        px: 1.5,
        mt: 1,
        mb: 1,
        width: "fit-content",
        display: "inline-flex"
    };

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                overflowY: "auto",
                borderRadius: 3,
                p: 2,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ textAlign: "left" }}>
                סקשנים להצגה
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 4,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {/* קבוצת מילון */}
                <Box sx={{ flex: 1, maxWidth: 170 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ textAlign: "left" }}>
                        סקשנים של מילון
                    </Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isGroupChecked(dictionarySections)}
                                    indeterminate={isGroupIndeterminate(dictionarySections)}
                                    onChange={() => handleGroupToggle(dictionarySections)}
                                />
                            }
                            label={<Typography fontWeight="bold">בחר הכל</Typography>}
                            sx={{ mb: 1 }}
                        />
                        {dictionarySections.map(({ value, label }) => (
                            <FormControlLabel
                                key={value}
                                control={
                                    <Checkbox
                                        checked={isChecked(value)}
                                        onChange={() => handleChange(value)}
                                        color="primary"
                                    />
                                }
                                label={label}
                                sx={{ mb: 0.0 }}
                            />
                        ))}
                    </FormGroup>
                </Box>

                {/* קבוצת תרגול */}
                <Box sx={{ flex: 1, maxWidth: 260 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ textAlign: "left" }}>
                        סקשנים של תרגול
                    </Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isGroupChecked(practiceSections)}
                                    indeterminate={isGroupIndeterminate(practiceSections)}
                                    onChange={() => handleGroupToggle(practiceSections)}
                                />
                            }
                            label={<Typography fontWeight="bold">בחר הכל</Typography>}
                            sx={{ mb: 1 }}
                        />
                        {practiceSections.map(({ value, label }) => (
                            <FormControlLabel
                                key={value}
                                control={
                                    <Checkbox
                                        checked={isChecked(value)}
                                        onChange={() => handleChange(value)}
                                        color="primary"
                                    />
                                }
                                label={label}
                                sx={{ mb: 0.0 }}
                            />
                        ))}
                    </FormGroup>
                </Box>
            </Box>
        </Box>
    );
});

export default SectionSelector;
